import { ApiError } from "@workspace/api-client";

type ValidationEntry = {
  path?: Array<string | number>;
  message?: string;
};

const FIELD_LABELS: Record<string, string> = {
  dni: "DNI",
  cuit: "CUIT",
  id: "ID",
  legajo: "Legajo",
};

export class HttpAppError extends Error {
  status: number;
  code?: string;
  details?: unknown;

  constructor(message: string, options: { status: number; code?: string; details?: unknown }) {
    super(message);
    this.name = "HttpAppError";
    this.status = options.status;
    this.code = options.code;
    this.details = options.details;
  }
}

export interface AppErrorInfo {
  status?: number;
  code?: string;
  message: string;
  details?: unknown;
}

export interface HumanErrorOptions {
  action?: string;
}

export interface HumanErrorResult {
  title: string;
  description: string;
}

function prettifyPath(path?: Array<string | number>) {
  if (!path || path.length === 0) return "formulario";
  return path
    .map((segment) => {
      const raw = String(segment);
      const lowered = raw.toLowerCase();
      if (FIELD_LABELS[lowered]) return FIELD_LABELS[lowered];
      return raw.replace(/([A-Z])/g, " $1").replace(/[_-]/g, " ").trim();
    })
    .join(" > ")
    .replace(/^./, (letter) => letter.toUpperCase());
}

function parseValidationMessage(message: string) {
  try {
    const parsed = JSON.parse(message) as ValidationEntry[];
    if (!Array.isArray(parsed) || parsed.length === 0) return null;

    const lines = parsed.slice(0, 3).map((entry) => `${prettifyPath(entry.path)}: ${entry.message ?? "Dato inválido"}`);
    const extra = parsed.length > 3 ? ` y ${parsed.length - 3} más.` : "";
    return `Revisá estos datos: ${lines.join(" · ")}${extra}`;
  } catch {
    const normalized = message
      .replace(/Expected string, received null/gi, "No puede estar vacío")
      .replace(/Expected string, received undefined/gi, "Campo obligatorio")
      .replace(/Expected number, received null/gi, "No puede estar vacío")
      .replace(/Expected number, received undefined/gi, "Campo obligatorio");

    if (normalized !== message) {
      return `Revisá estos datos: ${normalized}`;
    }

    return null;
  }
}

function normalizeUnknownError(error: unknown): AppErrorInfo {
  if (error instanceof HttpAppError) {
    return {
      status: error.status,
      code: error.code,
      message: error.message,
      details: error.details,
    };
  }

  if (error instanceof ApiError) {
    const data = error.data as { error?: { code?: string; message?: string } } | undefined;
    return {
      status: error.status,
      code: data?.error?.code,
      message: data?.error?.message ?? error.message,
      details: error.data,
    };
  }

  if (error instanceof Error) {
    return { message: error.message };
  }

  return { message: "Se produjo un error inesperado." };
}

export function getHumanError(error: unknown, options: HumanErrorOptions = {}): HumanErrorResult {
  const normalized = normalizeUnknownError(error);
  const action = options.action ? options.action.charAt(0).toLowerCase() + options.action.slice(1) : "continuar";
  const fallback: HumanErrorResult = {
    title: `No pudimos ${action}`,
    description: normalized.message || "Probá de nuevo en unos segundos.",
  };

  switch (normalized.code) {
    case "UNAUTHORIZED":
    case "INVALID_TOKEN":
      return {
        title: "Tu sesión ya no es válida",
        description: "Volvé a iniciar sesión para seguir trabajando sin perder el contexto.",
      };
    case "INVALID_CREDENTIALS":
      return {
        title: "No pudimos iniciar sesión",
        description: "Revisá tu email, legajo, contraseña o PIN e intentá otra vez.",
      };
    case "FORBIDDEN":
      return {
        title: "No tenés permiso para esta acción",
        description: "Si necesitás acceso, pedilo a un administrador del sistema.",
      };
    case "RATE_LIMIT":
      return {
        title: "Demasiados intentos seguidos",
        description: "Esperá un momento antes de volver a intentarlo.",
      };
    case "NOT_FOUND":
      return {
        title: "No encontramos lo que buscabas",
        description: normalized.message || "Es posible que ya no exista o que otra persona lo haya modificado.",
      };
    case "INVALID_ID":
      return {
        title: "El identificador no es válido",
        description: "Recargá la vista e intentá nuevamente.",
      };
    case "VALIDATION_ERROR": {
      const validation = parseValidationMessage(normalized.message);
      return {
        title: "Hay datos para corregir",
        description: validation ?? "Revisá los campos obligatorios e intentá nuevamente.",
      };
    }
    case "MISSING_REQUIRED_FIELDS":
      return {
        title: "Faltan datos obligatorios",
        description: normalized.message || "Completá los campos pendientes para continuar.",
      };
    case "MISSING_LEG_LOCATION":
      return {
        title: "Faltan plantas de descarga",
        description: normalized.message,
      };
    case "MISSING_CERTIFICATIONS":
      return {
        title: "El chofer no cumple los requisitos",
        description: normalized.message,
      };
    case "DRIVER_ON_VACATION":
      return {
        title: "El chofer no está disponible",
        description: "Tiene vacaciones registradas para esa fecha. Elegí otro chofer o mové el servicio.",
      };
    case "SCHEDULING_CONFLICT":
      return {
        title: "Hay un solapamiento de horario",
        description: normalized.message || "El chofer o el vehículo ya tienen otro servicio en ese horario. Ajustá hora, chofer o camión.",
      };
    case "OUT_OF_ORDER":
      return {
        title: "El orden operativo no cierra",
        description: normalized.message,
      };
    case "INVALID_STATE":
      return {
        title: "La acción no aplica en este estado",
        description: (normalized.message ?? "").replace(/'en_curso'/g, "'en curso'"),
      };
    case "SERVICE_IN_PROGRESS":
      return {
        title: "El servicio está en curso",
        description: "Este servicio ya está en ejecución. Para desasignarlo confirmá la acción desde el panel del servicio.",
      };
    case "INVALID_LEGS":
      return {
        title: "La secuencia de tramos no es válida",
        description: normalized.message,
      };
    case "INVALID_ASSIGNEE":
      return {
        title: "No se puede asignar ese usuario",
        description: normalized.message,
      };
    case "INVALID_CERTIFICATION_DATES":
    case "INVALID_CERTIFICATION_RULES":
      return {
        title: "Las reglas de certificación no cierran",
        description: normalized.message,
      };
    case "MISSING_CREDENTIALS":
    case "MISSING_PASSWORD":
    case "BAD_REQUEST":
      return {
        title: `No pudimos ${action}`,
        description: normalized.message,
      };
    case "MISSING_PERSONNEL":
      return {
        title: "Falta vincular personal",
        description: "Para crear este tipo de usuario es obligatorio asociarlo a un registro de personal.",
      };
    case "ROLE_MISMATCH":
      return {
        title: "El rol no coincide con el personal",
        description: normalized.message || "El rol asignado no es compatible con el tipo de personal seleccionado.",
      };
    case "MISSING_EMAIL":
      return {
        title: "El personal no tiene email",
        description: "Para dar acceso al panel, el registro de personal debe tener un email cargado.",
      };
    case "MISSING_LEGAJO":
      return {
        title: "El personal no tiene nombre de usuario",
        description: "Para dar acceso a la app de chofer, el registro de personal debe tener un legajo/usuario cargado.",
      };
    case "MISSING_PIN":
      return {
        title: "Falta el PIN",
        description: "El acceso de chofer requiere un PIN de 4 dígitos.",
      };
    case "PERSONNEL_ALREADY_LINKED":
      return {
        title: "El personal ya tiene un usuario",
        description: "Ese registro de personal ya tiene un acceso activo vinculado.",
      };
    case "USER_INACTIVE":
      return {
        title: "El usuario está desactivado",
        description: "No se pueden realizar acciones sobre un usuario eliminado.",
      };
    case "DUPLICATE_EMAIL":
      return {
        title: "El email ya está en uso",
        description: "Ya existe un usuario registrado con ese email. Verificá si fue eliminado previamente.",
      };
    case "DUPLICATE_LEGAJO":
      return {
        title: "El nombre de usuario ya está en uso",
        description: "Ya existe un usuario registrado con ese nombre de usuario (legajo).",
      };
    case "INTERNAL_ERROR":
      return {
        title: "Algo falló del lado del servidor",
        description: "No es un problema de tus datos. Probá otra vez en unos segundos.",
      };
    default:
      return fallback;
  }
}

export function getErrorDescription(error: unknown) {
  return getHumanError(error).description;
}
